import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import type { DidUser, SmsDid } from "../../interfaces/Did.type";
import { incomingMessage } from "../../interfaces/messages.type";

export type SelectedDid = string | null;
 interface SmsDidsState {
  data: SmsDid[];
  selectedDid: SelectedDid;
  didUsers: DidUser[];
}

const initialState: SmsDidsState = {
  data: [],
  selectedDid: null,
  didUsers: [],
};

export const smsDidsSlice = createSlice({
  name: "dids",
  initialState,
  reducers: {
    /**
     * Reducer function to set the user DIDs list in the store.
     *
     * @param {SmsDidsState} state - Current user DIDs state in the store
     * @param {PayloadAction<Did[]>} action - Action containing the new user DIDs list
     *
     * @returns {void}
     */
    setSmsDids(state, action: PayloadAction<SmsDid[]>): void {
      // Map the new user DIDs list to a new array of objects with the "total" and "unread"
      // fields cast to numbers (as they are received as strings from the backend)
      state.data = action.payload.map((userDid) => {
        return {
          ...userDid,
          total: Number(userDid.total),
          unread_messages: Number(userDid.unread_messages_count),
          unread_conversations: Number(userDid.unread_conversations_count),
        };
      });

  
    },

    setDidUsersSlice(state: SmsDidsState, action: PayloadAction<DidUser[]>): void {
      state.didUsers = action.payload;
    },

    updateSmsDidCounterByIncomingMessage(state, action: PayloadAction<incomingMessage>): void {
      const index = state.data.findIndex(
        (userDid) => userDid.did === action.payload.did
      );
      if (index !== -1) {
        state.data[index].unread_messages_count =
          action.payload.direction == "inbound"
            ? Number(state.data[index].unread_messages_count) + 1
            : Number(state.data[index].unread_messages_count);
      }
    },

    decreasingReadCounterSmsDid(
      state,
      action: PayloadAction<{ did: string, counter: number }>,
    ): void {
      const index = state.data.findIndex(
        (userDid) => userDid.did === action.payload.did
      );

      if (index !== -1) {
        state.data[index].unread_messages_count = Math.max(
          Number(state.data[index].unread_messages_count) - action.payload.counter,
          0
        );
      }
    },

    updateReadFlagSmsDid(
      state,
      action: PayloadAction<{ did: string; unread: boolean }>
    ): void {
      const index = state.data.findIndex(
        (userDid) => userDid.did === action.payload.did
      );

      if (index !== -1) {
        if (action.payload.unread) {
          state.data[index].unread_conversations_count = Number(state.data[index].unread_conversations_count) + 1;
        } else {
          state.data[index].unread_conversations_count = Math.max(Number(state.data[index].unread_conversations_count) - 1, 0);
        }
      }
    },

    /**
     * Reducer function to set the selected DID in the store.
     *
     * @param {UserDidsState} state - Current user DIDs state in the store
     * @param {PayloadAction<string | null>} action - Action containing the new selected DID
     *
     * @returns {void}
     */
    setSelectedDid(state, action: PayloadAction<string | null>): void {
      /**
       * Set the new selected DID in the store.
       */
      state.selectedDid = action.payload;
    },
  },
});

export const {
  setSmsDids,
  setSelectedDid,
  updateSmsDidCounterByIncomingMessage,
  decreasingReadCounterSmsDid,
  updateReadFlagSmsDid
} = smsDidsSlice.actions;

export default smsDidsSlice.reducer;
